import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, TypeBadge, fmtDate } from "../components/common";
import { SeverityBadge } from "../components/SeverityBadge";
import RoEGate from "../components/RoEGate";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Scan, Plus, MagnifyingGlass, Bug, Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";

const SCAN_TYPES = [
  { v: "discovery", label: "Discovery", desc: "Host & port scanning, service/OS fingerprinting (Nmap)", icon: MagnifyingGlass, color: "#3b82f6" },
  { v: "vulnerability", label: "Vulnerability", desc: "CVE detection & CVSS scoring (OpenVAS/Greenbone)", icon: Bug, color: "#eab308" },
  { v: "exploitation", label: "Exploitation", desc: "Controlled active testing — requires RoE sign-off", icon: Crosshair, color: "#ef4444" },
];

export default function Scans() {
  const { user } = useAuth();
  const [scans, setScans] = useState([]);
  const [targets, setTargets] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ workspace_id: "", target_id: "", type: "discovery" });
  const [roeOpen, setRoeOpen] = useState(false);
  const [roeTarget, setRoeTarget] = useState(null);
  const canManage = user.role === "admin" || user.role === "pentester";

  const load = useCallback(() => {
    api.get("/scans").then((r) => setScans(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/targets").then((r) => setTargets(r.data));
    api.get("/workspaces").then((r) => setWorkspaces(r.data));
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const wsTargets = targets.filter((t) => t.workspace_id === form.workspace_id);

  const startScan = async () => {
    const target = targets.find((t) => t.id === form.target_id);
    if (!target) return;
    if (form.type === "exploitation") {
      setRoeTarget(target);
      setOpen(false);
      setRoeOpen(true);
      return;
    }
    try {
      await api.post("/scans", { workspace_id: form.workspace_id, target_id: form.target_id, type: form.type });
      toast.success(`${form.type} scan queued`);
      setOpen(false);
      setForm({ workspace_id: "", target_id: "", type: "discovery" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="fade-up">
      <PageHeader
        overline="Orchestration"
        title="Scans"
        subtitle="Queue and monitor server-side scans with live progress. Exploitation is gated by Rules of Engagement."
        action={
          canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-scan-button" className="bg-blue-600 hover:bg-blue-500 text-white">
                  <Plus size={17} weight="bold" className="mr-1.5" /> New Scan
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0b1220] border-[#1e293b] text-white max-w-lg">
                <DialogHeader><DialogTitle>Configure Scan</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Workspace</Label>
                    <Select value={form.workspace_id}
                      onValueChange={(v) => setForm({ ...form, workspace_id: v, target_id: "" })}>
                      <SelectTrigger data-testid="scan-workspace-select" className="mt-1 bg-[#0f172a] border-[#1e293b]">
                        <SelectValue placeholder="Select workspace" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0b1220] border-[#1e293b] text-white">
                        {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Target</Label>
                    <Select value={form.target_id} onValueChange={(v) => setForm({ ...form, target_id: v })}
                      disabled={!form.workspace_id}>
                      <SelectTrigger data-testid="scan-target-select" className="mt-1 bg-[#0f172a] border-[#1e293b]">
                        <SelectValue placeholder="Select target" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0b1220] border-[#1e293b] text-white">
                        {wsTargets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} — {t.value}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-2 block">Scan type</Label>
                    <div className="space-y-2">
                      {SCAN_TYPES.map((st) => (
                        <button key={st.v} type="button" data-testid={`scan-type-${st.v}`}
                          onClick={() => setForm({ ...form, type: st.v })}
                          className={`w-full text-left rounded-md border px-3 py-2.5 flex items-center gap-3 transition-colors duration-150 ${
                            form.type === st.v ? "border-blue-500 bg-blue-500/10" : "border-[#1e293b] hover:bg-slate-800/50"
                          }`}>
                          <st.icon size={20} weight="bold" style={{ color: st.color }} />
                          <div>
                            <div className="text-sm font-medium text-white flex items-center gap-2">
                              {st.label}
                              {st.v === "exploitation" && <span className="text-[10px] font-mono text-red-400 border border-red-500/40 rounded px-1">RoE</span>}
                            </div>
                            <div className="text-xs text-slate-500">{st.desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="scan-launch-button" onClick={startScan}
                    disabled={!form.workspace_id || !form.target_id}
                    className="bg-blue-600 hover:bg-blue-500 text-white">
                    {form.type === "exploitation" ? "Proceed to RoE Gate" : "Launch Scan"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {scans.length === 0 ? (
        <EmptyState icon={Scan} title="No scans yet" hint="Launch a discovery scan to begin mapping the environment." />
      ) : (
        <div className="space-y-3">
          {scans.map((s) => (
            <Card key={s.id} data-testid={`scan-card-${s.id}`}
              className="bg-[#0f172a] border-[#1e293b] rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <TypeBadge type={s.type} />
                  <div className="min-w-0">
                    <div className="text-white font-medium truncate">{s.target_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{s.target_value} · {fmtDate(s.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {s.status === "completed" && s.stats && (
                    <div className="flex items-center gap-1.5">
                      {["critical", "high", "medium", "low"].map((k) =>
                        s.stats[k] > 0 ? <SeverityBadge key={k} severity={k} testid={`scan-sev-${s.id}-${k}`} /> : null
                      )}
                      {sumStats(s.stats) === 0 && <span className="text-xs text-slate-500">No findings</span>}
                    </div>
                  )}
                  <StatusBadge status={s.status} testid={`scan-status-${s.id}`} />
                </div>
              </div>

              {(s.status === "running" || s.status === "queued") && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-blue-400 font-mono animate-pulse">{s.phase || "Queued"}</span>
                    <span className="text-slate-500 font-mono">{s.progress || 0}%</span>
                  </div>
                  <div className="relative overflow-hidden rounded-full">
                    <Progress value={s.progress || 0} className="h-1.5 bg-slate-800 [&>div]:bg-blue-500" />
                    <div className="scan-shimmer absolute inset-0 rounded-full pointer-events-none" />
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <RoEGate
        open={roeOpen}
        onOpenChange={setRoeOpen}
        target={roeTarget}
        workspaceId={form.workspace_id}
        onLaunched={() => { setForm({ workspace_id: "", target_id: "", type: "discovery" }); load(); }}
      />
    </div>
  );
}

function sumStats(s) {
  return Object.values(s || {}).reduce((a, b) => a + b, 0);
}
